import React, { useCallback, useEffect, useState } from "react";
import { Text, TouchableOpacity, View } from "react-native";
import { Icon } from "react-native-elements";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { t } from "../services/i18n";
import {
	isWebPushAvailable,
	registerWebPush
} from "../services/WebPushRegistration";
import commonStyles, { COLOR2, DARKGREY_COLOR } from "../styles/commonStyles";
import {
	getPermission,
	isPushSupported,
	needsInstallForPush,
	PERMISSION_DEFAULT,
	PERMISSION_DENIED,
	PERMISSION_GRANTED
} from "../utils/WebPush";

/**
 * Permanent entry point for browser notifications, next to the alert
 * preferences they gate: the preferences below are pointless while the browser
 * itself has not been allowed to deliver anything.
 *
 * It also states why the button is unavailable — the permission was refused, or
 * the PWA has not been installed yet, which iOS requires before it will deliver
 * Web Push at all.
 */
const WebPushSettingRow = () => {
	const dispatch = useDispatch();
	const [permission, setPermission] = useState(getPermission());
	const { user, lang } = useSelector(
		state => ({
			user: state.profileReducer.user,
			lang: state.globalInformationsReducer.lang
		}),
		shallowEqual
	);

	const refresh = useCallback(() => setPermission(getPermission()), []);

	useEffect(() => {
		refresh();
		// The permission can be changed from the browser UI while the app stays
		// open, and Chromium fires `change` on the permission status for it.
		if (!navigator.permissions?.query) {
			return undefined;
		}
		let status;
		let cancelled = false;
		navigator.permissions
			.query({ name: "notifications" })
			.then(result => {
				if (cancelled) {
					return;
				}
				status = result;
				status.addEventListener("change", refresh);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
			status?.removeEventListener("change", refresh);
		};
	}, [refresh]);

	if (!user || !isPushSupported()) {
		return null;
	}

	const { font, darkgrey, row, alignItemsCenter, mb10 } = commonStyles;

	const enable = async () => {
		await registerWebPush(dispatch, lang, { promptIfNeeded: true });
		refresh();
	};

	const renderStatus = () => {
		if (permission === PERMISSION_GRANTED) {
			return (
				<View style={[row, alignItemsCenter]}>
					<Icon
						name="bell-check-outline"
						type="material-community"
						size={18}
						color={COLOR2}
					/>
					<Text style={[font, styles.statusText, { color: COLOR2 }]}>
						{t("notifications:statusGranted")}
					</Text>
				</View>
			);
		}
		if (permission === PERMISSION_DENIED) {
			return (
				<Text style={[font, styles.hint]}>
					{t("notifications:blockedHint")}
				</Text>
			);
		}
		// `default`: either offer the prompt, or explain that iOS needs the PWA
		// installed before it will hand out a subscription.
		if (needsInstallForPush() || !isWebPushAvailable()) {
			return (
				<Text style={[font, styles.hint]}>
					{t("notifications:installHint")}
				</Text>
			);
		}
		return (
			<TouchableOpacity onPress={enable} style={styles.enableButton}>
				<Text style={styles.enableButtonText}>
					{t("notifications:enableButton")}
				</Text>
			</TouchableOpacity>
		);
	};

	return (
		<View style={mb10}>
			<Text style={[font, darkgrey, styles.title]}>
				{t("notifications:sectionTitle")}
			</Text>
			{renderStatus()}
		</View>
	);
};

const styles = {
	title: {
		fontSize: 18,
		marginTop: 15,
		marginBottom: 10
	},
	hint: {
		fontSize: 13,
		color: DARKGREY_COLOR,
		lineHeight: 18
	},
	statusText: {
		fontSize: 13,
		marginLeft: 6
	},
	enableButton: {
		alignSelf: "flex-start",
		backgroundColor: COLOR2,
		borderRadius: 6,
		paddingHorizontal: 14,
		paddingVertical: 8
	},
	enableButtonText: {
		fontSize: 13,
		fontWeight: "600",
		color: "#fff"
	}
};

export default WebPushSettingRow;
